import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'

import { isSavingAtom } from '@/atoms/_editorAtoms'
import {
  editorTextBoxConvertSkipConfirmationAtom,
  showEditorTextBoxConvertModalForItemIdAtom,
} from '@/atoms/itemControlAtoms'
import { FullscreenModal } from '@/components/global/FullscreenModal/FullscreenModal'
import { Button } from '@/components/priority/Button/Button'
import { Flex } from '@/components/priority/Flex/Flex'
import { Text } from '@/components/priority/Text/Text'
import { useEditorConvertTextBox } from '@/hooks/editor/useEditorConvertTextBox'
import * as S from './EditorTextBoxConvertModal.styled'

/**
 * The confirmation before a TinyMCE text item turns into a Lotus text box,
 * in the shape of the Make editable warning: what happens, and that the
 * editor has no way back. Confirm runs the conversion, and waits while
 * another Save is writing the line, since the conversion is a Save of its
 * own; Cancel, the close control or a click outside leaves the item as it
 * is. Its Stop seeing this? box, unchecked each time it opens, counts only
 * on Confirm: it stops the confirmation in this browser
 * (`editorTextBoxConvertSkipConfirmationAtom`). It is an editor panel
 * (`data-editor-ui`), so a click on it keeps the item selected.
 */
export const EditorTextBoxConvertModal = () => {
  const { t } = useTranslation()
  const [itemId, setItemId] = useRecoilState(
    showEditorTextBoxConvertModalForItemIdAtom,
  )
  const setSkipConfirmation = useSetRecoilState(
    editorTextBoxConvertSkipConfirmationAtom,
  )
  const isSaving = useRecoilValue(isSavingAtom)
  const convertTextBox = useEditorConvertTextBox()
  const [stopShowing, setStopShowing] = useState(false)

  useEffect(() => {
    setStopShowing(false)
  }, [itemId])

  if (itemId === null) return null

  const close = () => setItemId(null)
  const confirm = () => {
    if (stopShowing) setSkipConfirmation(true)
    setItemId(null)
    void convertTextBox(itemId)
  }

  return createPortal(
    <FullscreenModal
      show={[true, close]}
      hideClose={false}
      data-editor-ui="editor-text-box-convert-confirmation"
    >
      <Flex
        column
        full
        padding="32px"
        maxWidth="600px"
        data-testid="editor-text-box-convert-modal"
      >
        <Text size="20px" weight="600" margin="10px 0 10px 0">
          {t('editor.textBox.convertFromTinyMce.title')}
        </Text>
        <Text size="16px" margin="10px 0 10px 0">
          {t('editor.textBox.convertFromTinyMce.message')}
        </Text>
        <Flex justify="space-between" align="center" margin="10px 0 0 0">
          <S.StopShowingLabel>
            <S.StopShowingCheckbox
              type="checkbox"
              checked={stopShowing}
              onChange={(event) => setStopShowing(event.target.checked)}
              data-testid="editor-text-box-convert-stop-showing"
            />
            <Text size="14px">
              {t('editor.textBox.convertFromTinyMce.stopShowing')}
            </Text>
          </S.StopShowingLabel>
          <Flex>
            <Button
              margin="0 16px 0 0"
              onClick={close}
              data-testid="editor-text-box-convert-cancel"
            >
              {t('editor.textBox.convertFromTinyMce.cancel')}
            </Button>
            <Button
              primary
              margin="0 16px 0 0"
              onClick={confirm}
              disabled={isSaving}
              data-testid="editor-text-box-convert-confirm"
            >
              {t('editor.textBox.convertFromTinyMce.confirm')}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </FullscreenModal>,
    document.body,
  )
}
